/**
 * Per-user settings.
 * - Connect / disconnect Outlook mailbox
 * - View Aircall status (org-level, read-only)
 * - Change password (redirect to Security page — TBD)
 */
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  Mail,
  Phone,
  CheckCircle2,
  Loader2,
  Trash2,
  LogIn,
  Key,
  Palette,
  Sun,
  Moon,
  Droplets,
  RefreshCw,
  PenLine,
} from "lucide-react"
import { useTheme, type Theme } from "@/hooks/useTheme"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  integrationsApi,
  authApi,
  ApiError,
  api,
  templatesApi,
  automationsApi,
} from "@/lib/api"
import { RichTextEditor } from "@/components/RichTextEditor"
import { useAuth } from "@/hooks/useAuth"
import { timeAgo } from "@shared/utils/date"
import { cn } from "@/lib/utils"

export default function MySettingsPage() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const connectedParam = searchParams.get("connected")
  const [banner, setBanner] = useState<string | null>(null)

  useEffect(() => {
    if (!connectedParam) return
    setBanner(`✓ ${connectedParam[0]!.toUpperCase()}${connectedParam.slice(1)} connected successfully`)
    const t = setTimeout(() => {
      setBanner(null)
      const next = new URLSearchParams(searchParams)
      next.delete("connected")
      setSearchParams(next, { replace: true })
    }, 5000)
    return () => clearTimeout(t)
  }, [connectedParam])

  const status = useQuery({
    queryKey: ["integration-status"],
    queryFn: () => integrationsApi.status(),
  })

  return (
    <AppShell title="My Settings" subtitle="Personal integrations and account settings">
      <div className="max-w-2xl space-y-6">
        {banner && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            {banner}
          </div>
        )}

        {/* Profile summary */}
        {user && (
          <Card className="p-5">
            <div className="flex items-center gap-4">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-rc-blue-100 text-rc-blue-700 font-semibold">
                {user.fullName
                  .split(" ")
                  .slice(0, 2)
                  .map((n) => n[0]!.toUpperCase())
                  .join("")}
              </div>
              <div>
                <div className="font-medium text-foreground">{user.fullName}</div>
                <div className="text-muted-foreground text-sm">{user.email}</div>
                <Badge variant="muted" className="mt-1 text-[10px]">
                  {user.role}
                </Badge>
              </div>
            </div>
          </Card>
        )}

        {/* Appearance / theme picker */}
        <AppearanceCard />

        {/* Change password */}
        <ChangePasswordCard />

        {/* Outlook */}
        <OutlookUserCard
          status={status.data ?? null}
          isLoading={status.isLoading}
        />

        {/* Email signature */}
        <EmailSignatureCard />

        {/* Aircall (org read-only) */}
        <AircallStatusCard status={status.data ?? null} isLoading={status.isLoading} />
      </div>
    </AppShell>
  )
}

// ============================================================
// Appearance / theme picker
// ============================================================
function AppearanceCard() {
  const { theme, setTheme } = useTheme()

  const options: { value: Theme; label: string; description: string; icon: typeof Sun }[] = [
    {
      value: "rivercity",
      label: "Rivercity",
      description: "Brand look — dark navy sidebar, light content.",
      icon: Droplets,
    },
    {
      value: "light",
      label: "Light",
      description: "Light sidebar and content. Easy on daytime eyes.",
      icon: Sun,
    },
    {
      value: "dark",
      label: "Dark",
      description: "Full dark mode. Great for late-night sessions.",
      icon: Moon,
    },
  ]

  return (
    <Card className="p-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-rc-navy-100 text-rc-navy-700">
          <Palette className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-foreground">Appearance</h3>
          <p className="text-muted-foreground text-sm">
            Pick the theme you like best. Saved on this device.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {options.map((opt) => {
          const Icon = opt.icon
          const active = theme === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setTheme(opt.value)}
              className={`rounded-lg border p-3 text-left transition-colors ${
                active
                  ? "border-rc-blue-500 bg-rc-blue-50 ring-2 ring-rc-blue-500/30"
                  : "border-border hover:border-rc-blue-300 hover:bg-rc-grey-50"
              }`}
              aria-pressed={active}
            >
              <div className="flex items-center gap-2">
                <Icon
                  className={`h-4 w-4 ${active ? "text-rc-blue-700" : "text-muted-foreground"}`}
                />
                <span className="text-sm font-medium text-foreground">{opt.label}</span>
                {active && (
                  <CheckCircle2 className="ml-auto h-4 w-4 text-rc-blue-600" />
                )}
              </div>
              <p className="text-muted-foreground mt-1 text-xs">{opt.description}</p>
            </button>
          )
        })}
      </div>
    </Card>
  )
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("")
  const [next, setNext] = useState("")
  const [confirmed, setConfirmed] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState(false)

  const change = useMutation({
    mutationFn: (vars: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(vars.currentPassword, vars.newPassword),
    onSuccess: () => {
      setOk(true)
      setCurrent("")
      setNext("")
      setConfirmed("")
      setTimeout(() => setOk(false), 4000)
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Something went wrong.")
    },
  })

  return (
    <Card className="p-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-rc-navy-100 text-rc-navy-700">
          <Key className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-foreground">Change password</h3>
          <p className="text-muted-foreground text-sm">
            Use at least 12 characters. Your existing 2FA setup stays the same.
          </p>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          setError(null)
          if (next.length < 12) {
            setError("New password must be at least 12 characters.")
            return
          }
          if (next !== confirmed) {
            setError("Passwords don\u2019t match.")
            return
          }
          change.mutate({ currentPassword: current, newPassword: next })
        }}
        className="grid gap-3 sm:grid-cols-2"
      >
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="cp-current">Current password</Label>
          <Input
            id="cp-current"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cp-new">New password</Label>
          <Input
            id="cp-new"
            type="password"
            autoComplete="new-password"
            required
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cp-confirm">Confirm new password</Label>
          <Input
            id="cp-confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirmed}
            onChange={(e) => setConfirmed(e.target.value)}
          />
        </div>

        {error && (
          <div className="sm:col-span-2 border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
            {error}
          </div>
        )}

        {ok && (
          <div className="sm:col-span-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            ✓ Password updated.
          </div>
        )}

        <div className="sm:col-span-2">
          <Button type="submit" disabled={change.isPending}>
            {change.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Update password"
            )}
          </Button>
        </div>
      </form>
    </Card>
  )
}

function OutlookUserCard({
  status,
  isLoading,
}: {
  status: Awaited<ReturnType<typeof integrationsApi.status>> | null
  isLoading: boolean
}) {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const connected = status?.user.outlook?.isActive === true
  const orgConfigured =
    status?.envConfigured.outlook.clientId && status?.envConfigured.outlook.tenantId

  const authorize = useMutation({
    mutationFn: () => integrationsApi.outlookAuthorize(),
    onSuccess: (r) => {
      window.location.href = r.url
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Failed to start OAuth"),
  })

  const disconnect = useMutation({
    mutationFn: () => integrationsApi.disconnectOutlook(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integration-status"] }),
  })

  const [syncBanner, setSyncBanner] = useState<string | null>(null)
  const sync = useMutation({
    mutationFn: () => integrationsApi.outlookSync(),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["integration-status"] })
      qc.invalidateQueries({ queryKey: ["communications"] })
      if (r.error) {
        setSyncBanner(`⚠️ Sync error: ${r.error}`)
      } else {
        setSyncBanner(
          `✓ Synced. Fetched ${r.fetched}, matched ${r.matched} to contacts, ${r.unmatched} unmatched, ${r.skipped} skipped.`
        )
      }
      setTimeout(() => setSyncBanner(null), 6000)
    },
    onError: (err) => {
      setSyncBanner(
        err instanceof ApiError ? `⚠️ ${err.message}` : "⚠️ Sync failed"
      )
      setTimeout(() => setSyncBanner(null), 6000)
    },
  })

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-100 text-blue-700">
            <Mail className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Outlook</h3>
            <p className="text-muted-foreground text-sm">
              Sync your Microsoft 365 mailbox — matched emails appear on the correct contact.
            </p>
          </div>
        </div>
        {isLoading ? (
          <Badge variant="muted" className="text-xs">
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            Loading…
          </Badge>
        ) : connected ? (
          <Badge variant="success" className="text-xs">
            <CheckCircle2 className="mr-1 h-3 w-3" />
            Connected
          </Badge>
        ) : (
          <Badge variant="muted" className="text-xs">
            Not connected
          </Badge>
        )}
      </div>

      <div className="mt-4">
        {!orgConfigured ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            Outlook OAuth isn't set up yet. Ask your admin to configure the Microsoft Entra app in
            organisation Settings.
          </div>
        ) : connected ? (
          <div className="space-y-2 text-sm">
            <p>
              Connected as <strong>{status?.user.outlook?.externalAccountId}</strong>
            </p>
            {status?.user.outlook?.lastSyncAt && (
              <p className="text-muted-foreground text-xs">
                Last sync {timeAgo(status.user.outlook.lastSyncAt)}
              </p>
            )}
            {status?.user.outlook?.lastSyncError && (
              <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-xs">
                Sync error: {status.user.outlook.lastSyncError}
              </div>
            )}
            {syncBanner && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
                {syncBanner}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => sync.mutate()}
                disabled={sync.isPending}
              >
                {sync.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Sync now
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (confirm("Disconnect Outlook? Existing emails on contact timelines stay.")) {
                    disconnect.mutate()
                  }
                }}
                className="hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Disconnect
              </Button>
            </div>
            <p className="text-muted-foreground text-xs">
              Auto-sync runs every 15 minutes. Emails matching a contact by email address appear on their timeline; unmatched ones show up under Settings.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-muted-foreground text-sm">
              Click below to sign in with Microsoft. We'll only request read access to your mailbox.
            </p>
            {error && (
              <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
                {error}
              </div>
            )}
            <Button onClick={() => authorize.mutate()} disabled={authorize.isPending}>
              {authorize.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LogIn className="h-4 w-4" />
              )}
              Connect Outlook
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}

function AircallStatusCard({
  status,
  isLoading,
}: {
  status: Awaited<ReturnType<typeof integrationsApi.status>> | null
  isLoading: boolean
}) {
  const orgConnected = status?.org.aircall?.isActive === true

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-emerald-100 text-emerald-700">
            <Phone className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Aircall</h3>
            <p className="text-muted-foreground text-sm">
              Calls are logged automatically for the whole organisation.
            </p>
          </div>
        </div>
        {isLoading ? (
          <Badge variant="muted" className="text-xs">
            Loading…
          </Badge>
        ) : orgConnected ? (
          <Badge variant="success" className="text-xs">
            <CheckCircle2 className="mr-1 h-3 w-3" />
            Active
          </Badge>
        ) : (
          <Badge variant="muted" className="text-xs">
            Not connected
          </Badge>
        )}
      </div>
      <p className="text-muted-foreground mt-3 text-sm">
        Aircall is configured per organisation. Ask your admin to connect it in{" "}
        <strong>Settings</strong>.
      </p>
    </Card>
  )
}

// ============================================================
// Email signature
// ============================================================
function EmailSignatureCard() {
  const qc = useQueryClient()
  const [html, setHtml] = useState("")
  const [loaded, setLoaded] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [status, setStatus] = useState<"idle" | "saved">("idle")
  const [error, setError] = useState<string | null>(null)

  const sigQ = useQuery({
    queryKey: ["my-signature"],
    queryFn: () =>
      api.get<{ signature: { html: string } | null }>("/templates/-/signature"),
  })

  useEffect(() => {
    if (sigQ.data && !loaded) {
      setHtml(sigQ.data.signature?.html ?? "")
      setLoaded(true)
    }
  }, [sigQ.data, loaded])

  const save = useMutation({
    mutationFn: () => templatesApi.saveSignature(html),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-signature"] })
      qc.invalidateQueries({ queryKey: ["email-signature"] })
      setDirty(false)
      setStatus("saved")
      setError(null)
      setTimeout(() => setStatus("idle"), 2500)
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not save signature"
      setError(msg)
    },
  })

  // Rough byte estimate of the payload so we can warn before hitting the limit.
  const sizeKb = Math.round(new Blob([html]).size / 1024)
  const sizeWarn = sizeKb > 400
  const sizeOver = sizeKb > 512

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PenLine className="text-rc-blue-600 h-5 w-5" />
          <div>
            <h2 className="font-semibold text-foreground">Email Signature</h2>
            <p className="text-muted-foreground text-xs">
              Appended to automation and template emails when “Include my
              signature” is on.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status === "saved" && (
            <span className="text-emerald-700 flex items-center gap-1 text-xs">
              <CheckCircle2 className="h-3.5 w-3.5" /> Saved
            </span>
          )}
          {dirty && <Badge variant="muted" className="text-[10px]">Unsaved</Badge>}
          <Button
            size="sm"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </div>

      {sigQ.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading signature…
        </div>
      ) : (
        <RichTextEditor
          value={html}
          onChange={(v) => {
            setHtml(v)
            setDirty(true)
            if (error) setError(null)
          }}
          placeholder="Regards, Your Name…"
          minHeight={180}
          onImageUpload={async (file) => {
            const r = await automationsApi.uploadInlineImage(file)
            return r.url
          }}
        />
      )}

      {error && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-800">
          <strong>Save failed:</strong> {error}
        </div>
      )}

      <div className="mt-2 flex items-center justify-between text-[11px]">
        <p className="text-muted-foreground">
          Use merge fields like <code>{`{{broker.firstName}}`}</code>. Add
          images via the image button in the toolbar — they upload to secure
          hosting automatically, so your emails stay small and deliverable.
          Max 5 MB per image.
        </p>
        <span
          className={cn(
            "shrink-0 ml-2",
            sizeOver
              ? "text-red-700 font-semibold"
              : sizeWarn
                ? "text-amber-700"
                : "text-muted-foreground"
          )}
        >
          {sizeKb} KB / 512 KB
        </span>
      </div>
    </Card>
  )
}
